import { Box, Flex, HStack, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import CDS from "design-system";
import AppPage from "layouts/AppPage";
import { useNavigate, useParams } from "react-router";
import { Heading18 } from "components/Heading";
import { useCallback, useEffect, useMemo } from "react";
import { FiPlus } from "react-icons/fi";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import NewTemplateButton from "features/Resources/SharedTemplate/Components/NewTemplateButton";
import CreatePathButtonWithModal from "features/Path/Components/Button/CreatePathButtonWithModal";
import PathsListView from "features/Path/Components/PathsList/PathsListView";
import { useV2PathListQuery } from "features/Path/hooks";
import { ActionType } from "features/Resources/Shared/models";
import useModal from "hooks/useModal";
import { PrimaryButton, TertiaryButton } from "components/Button";
import { generatePath } from "helpers/url";
import ListEmailTemplates from "./ListEmailTemplates";
import ListMessageTemplates from "./ListMessageTemplates";
import ListTaskTemplates from "./ListTaskTemplates";
import ListMeetingTemplates from "./ListMeetingTemplates";
import ListCheckpointTemplates from "./ListCheckpointTemplates";
import ListCustomForms from "./ListCustomForms";
import ImportMeetingForm from "./ImportMeetingForm";

const TAB_DESCRIPTIONS: Record<string, string> = {
  paths: "Reusable sequences of tasks, emails, messages, meetings, and checkpoints assigned to new hires.",
  emails: "Email templates you can add to paths.",
  messages: "Slack message templates you can add to paths.",
  tasks: "Task templates you can add to paths.",
  meetings: "Meeting templates you can add to paths.",
  checkpoints: "Checkpoint templates you can add to paths.",
  forms: "Forms collect structured information from new hires through complete-form tasks.",
};

export default function ListTemplatesPage() {
  const { type: resourceType } = useParams();
  const navigate = useNavigate();
  const { data: pathsData } = useV2PathListQuery("");

  const {
    open: openImportMeetingModal,
    close: closeImportMeetingModal,
    Modal: ImportMeetingModal,
  } = useModal({
    headerTitle: "Import Meeting",
    headerFontSize: "14px",
    size: "xl",
    isCentered: true,
    queryParam: "import",
  });

  const memoizedTabs = useMemo(
    () => [
      { path: "paths" },
      { path: "emails" },
      { path: "messages" },
      { path: "tasks" },
      { path: "meetings" },
      { path: "checkpoints" },
      { path: "forms" },
    ],
    [],
  );

  const isFormsTab = resourceType === "forms";
  const isPathsTab = resourceType === "paths";
  const actionType = resourceType?.replace(/s$/, "") as ActionType;

  // Find the index of the active tab
  const activeTabIndex = memoizedTabs.findIndex((tab) => tab.path === resourceType);

  useEffect(() => {
    if (activeTabIndex === -1) {
      navigate(`${PEOPLE_TEAM_ROUTES.planning.index}/paths`, { replace: true });
    }
  }, [activeTabIndex, navigate]);

  // Handle tab change to navigate to the correct route
  const handleTabChange = useCallback(
    (index: number) => {
      navigate(`${PEOPLE_TEAM_ROUTES.planning.index}/${memoizedTabs[index].path}`);
    },
    [navigate, memoizedTabs],
  );

  const handleImportMeeting = useCallback(
    ({ templateId }: { templateId: string }) => {
      closeImportMeetingModal();
      navigate(
        generatePath({
          path: PEOPLE_TEAM_ROUTES.meetingTemplates.show,
          params: { id: templateId },
          query: "import=true",
        }),
      );
    },
    [navigate, closeImportMeetingModal],
  );

  return (
    <AppPage>
      <Box my="4">
        <Flex justify="space-between">
          <Heading18>Planning</Heading18>
          <HStack>
            {actionType === "meeting" && (
              <PrimaryButton size="sm" onClick={openImportMeetingModal}>
                Import Meeting
              </PrimaryButton>
            )}
            {isPathsTab && <CreatePathButtonWithModal variant="primary" />}
            {isFormsTab && (
              <TertiaryButton leftIcon={<FiPlus />} size="sm" onClick={() => navigate(PEOPLE_TEAM_ROUTES.customForms.new)}>
                New Form
              </TertiaryButton>
            )}
            {!isPathsTab && !isFormsTab && <NewTemplateButton actionType={actionType} />}
          </HStack>
        </Flex>
        {resourceType && TAB_DESCRIPTIONS[resourceType] && (
          <CDS.Text12 color="gray.500" mt="1">
            {TAB_DESCRIPTIONS[resourceType]}
          </CDS.Text12>
        )}
      </Box>
      <Tabs index={activeTabIndex} onChange={handleTabChange} position="relative" variant="underline" isLazy>
        <TabList>
          <Tab>
            Paths
            {pathsData && (
              <CDS.Text12 as="span" ml="1.5" color="gray.400">
                {pathsData.length}
              </CDS.Text12>
            )}
          </Tab>
          <Box alignSelf="center" h="16px" borderLeftWidth="1px" borderColor="gray.300" mx="2" aria-hidden />
          <Tab>Emails</Tab>
          <Tab>Messages</Tab>
          <Tab>Tasks</Tab>
          <Tab>Meetings</Tab>
          <Tab>Checkpoints</Tab>
          <Tab>Forms</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <PathsListView />
          </TabPanel>
          <TabPanel>
            <ListEmailTemplates />
          </TabPanel>
          <TabPanel>
            <ListMessageTemplates />
          </TabPanel>
          <TabPanel>
            <ListTaskTemplates />
          </TabPanel>
          <TabPanel>
            <ListMeetingTemplates />
          </TabPanel>
          <TabPanel>
            <ListCheckpointTemplates />
          </TabPanel>
          <TabPanel>
            <ListCustomForms />
          </TabPanel>
        </TabPanels>
      </Tabs>
      <ImportMeetingModal>
        <ImportMeetingForm onComplete={handleImportMeeting} />
      </ImportMeetingModal>
    </AppPage>
  );
}
